import {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import type ErgAuthService from '@ergeon/erg-auth';

import {reportError} from '../../../utils/reporting';
import {askSrvAboutCurrentToken, hasLiveSession, watchUnauthorizedResponses} from '../../../utils/sessionExpiry';

type SessionNotice = 'none' | 'shown' | 'dismissed';

type RejectionReport = 'not-sent' | 'asking-srv' | 'sent' | 'srv-not-answering';

const UNANSWERED_CHECKS_PER_PAGE = 3;

type SessionExpiry = {
  sessionNoticeVisible: boolean;
  showSessionNotice(): void;
  dismissSessionNotice(): void;
  checkSessionQuietly(): void;
  onUnauthorized(): void;
};

const tabIsVisible = () => document.visibilityState === 'visible';

function useWhenTabBecomesVisible(callback: () => void) {
  useEffect(() => {
    const callWhenVisible = () => {
      if (tabIsVisible()) {
        callback();
      }
    };
    document.addEventListener('visibilitychange', callWhenVisible);

    return () => {
      document.removeEventListener('visibilitychange', callWhenVisible);
    };
  }, [callback]);
}

const reportStateAfterUnansweredCheck = (unansweredChecks: number): RejectionReport => {
  if (unansweredChecks < UNANSWERED_CHECKS_PER_PAGE) {
    return 'not-sent';
  }

  return 'srv-not-answering';
};

function useReportOfRejectedCurrentToken(authService: ErgAuthService) {
  const rejectionReport = useRef<RejectionReport>('not-sent');
  const unansweredChecks = useRef(0);

  return useCallback(async () => {
    if (rejectionReport.current !== 'not-sent') {
      return;
    }

    rejectionReport.current = 'asking-srv';
    const verdict = await askSrvAboutCurrentToken(authService);
    if (verdict === 'accepted') {
      rejectionReport.current = 'not-sent';
      return;
    }

    if (verdict === 'no-verdict') {
      unansweredChecks.current += 1;
      rejectionReport.current = reportStateAfterUnansweredCheck(unansweredChecks.current);
      return;
    }

    rejectionReport.current = 'sent';
    reportError(new Error('An API answered 401 under an unexpired ergauth token'), {
      operation: 'auth_unauthorized_with_live_token',
      handled: true,
    });
  }, [authService]);
}

function useSessionExpiry(authService: ErgAuthService): SessionExpiry {
  const [notice, setNotice] = useState<SessionNotice>('none');
  const currentNotice = useRef<SessionNotice>('none');
  const reportIfSrvRejectsCurrentToken = useReportOfRejectedCurrentToken(authService);

  const moveNoticeTo = useCallback((next: SessionNotice) => {
    if (currentNotice.current === next) {
      return;
    }

    currentNotice.current = next;
    setNotice(next);
  }, []);

  const checkSessionQuietly = useCallback(() => {
    if (hasLiveSession(authService)) {
      moveNoticeTo('none');
      return;
    }

    if (currentNotice.current === 'none') {
      moveNoticeTo('shown');
    }
  }, [authService, moveNoticeTo]);

  const checkSessionAloud = useCallback(() => {
    if (hasLiveSession(authService)) {
      moveNoticeTo('none');
      return;
    }

    moveNoticeTo('shown');
  }, [authService, moveNoticeTo]);

  const onUnauthorized = useCallback(() => {
    if (hasLiveSession(authService)) {
      void reportIfSrvRejectsCurrentToken();
    }
    checkSessionAloud();
  }, [authService, checkSessionAloud, reportIfSrvRejectsCurrentToken]);

  const showSessionNotice = useCallback(() => moveNoticeTo('shown'), [moveNoticeTo]);
  const dismissSessionNotice = useCallback(() => moveNoticeTo('dismissed'), [moveNoticeTo]);

  useWhenTabBecomesVisible(checkSessionAloud);
  useEffect(() => watchUnauthorizedResponses(axios, onUnauthorized), [onUnauthorized]);

  const sessionNoticeVisible = notice === 'shown';
  return {sessionNoticeVisible, showSessionNotice, dismissSessionNotice, checkSessionQuietly, onUnauthorized};
}

export default useSessionExpiry;
